
import React,{useState}from"react";
import{useNavigate}from"react-router-dom";
import{
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography
}from"@mui/material";
import{
  Visibility,
  VisibilityOff,
  MailOutlineRounded,
  LockOutlined
}from"@mui/icons-material";
import{useAuth}from"../context/AuthContext";
import logo from"../static/media/logo.webp";
import background from"../static/media/found.webp";

const PRIMARY="#65BDC3";
const PRIMARY_DARK="#368F96";
const TEXT="#243A40";
const SECONDARY_TEXT="#71848A";

export default function Login(){
  const navigate=useNavigate();
  const{login}=useAuth();

  const[form,setForm]=useState({
    email:"",
    password:""
  });

  const[showPassword,setShowPassword]=useState(false);
  const[loading,setLoading]=useState(false);
  const[error,setError]=useState("");

  const handleChange=event=>{
    const{name,value}=event.target;
    setForm(prev=>({
      ...prev,
      [name]:value
    }));
  };

  const parseJsonSafe=async response=>{
    try{
      return await response.json();
    }catch{
      return{};
    }
  };

  const handleSubmit=async event=>{
    event.preventDefault();
    setError("");

    if(!form.email.trim()||!form.password.trim()){
      setError("Ingresa tu correo y contraseña.");
      return;
    }

    try{
      setLoading(true);

      const email=form.email.trim().toLowerCase();

      const response=await fetch("/api/auth/login/",{
        method:"POST",
        headers:{
          "Content-Type":"application/json"
        },
        body:JSON.stringify({
          email,
          password:form.password
        })
      });

      const data=await parseJsonSafe(response);

      if(!response.ok||data.ok===false){
        throw new Error(
          data.detail||
          data.error||
          "No se pudo iniciar sesión."
        );
      }

      const finalUser=await login({
        access:data.access,
        refresh:data.refresh,
        user:data.user
      });

      const currentUser=finalUser||data.user||{};

      const requiresPasswordChange=
        data.requires_password_change===true||
        data.requiere_cambio_clave===true||
        data.must_change_password===true||
        data.change_password_required===true||
        data.temporary_password===true||
        currentUser.requires_password_change===true||
        currentUser.requiere_cambio_clave===true||
        currentUser.must_change_password===true||
        currentUser.change_password_required===true||
        currentUser.temporary_password===true||
        data.user?.requires_password_change===true||
        data.user?.requiere_cambio_clave===true||
        data.user?.must_change_password===true||
        data.user?.change_password_required===true||
        data.user?.temporary_password===true;

      if(requiresPasswordChange){
        navigate("/actualizar-password",{
          replace:true,
          state:{
            email:currentUser.email||email,
            accessToken:data.access,
            refreshToken:data.refresh,
            requiresPasswordChange:true
          }
        });
        return;
      }

      const role=currentUser.role||data.user?.role;

      if(["super_admin","admin","staff"].includes(role)){
        navigate("/admin-panel",{replace:true});
        return;
      }

      navigate("/",{replace:true});
    }catch(err){
      setError(
        err.message||
        "No se pudo iniciar sesión."
      );
    }finally{
      setLoading(false);
    }
  };

  const inputStyles={
    "& .MuiOutlinedInput-root":{
      height:46,
      borderRadius:"12px",
      backgroundColor:"#FFFFFF",
      fontSize:"0.9rem",
      fontWeight:400,
      transition:"border-color 0.2s ease",
      "& fieldset":{
        borderColor:"#DCE6E8"
      },
      "&:hover fieldset":{
        borderColor:PRIMARY
      },
      "&.Mui-focused fieldset":{
        borderColor:PRIMARY_DARK,
        borderWidth:"1.5px"
      }
    },
    "& .MuiInputLabel-root":{
      color:SECONDARY_TEXT,
      fontSize:"0.9rem",
      fontWeight:400,
      "&.Mui-focused":{
        color:PRIMARY_DARK
      }
    },
    "& .MuiInputBase-input":{
      color:TEXT,
      fontWeight:400
    },
    "& input:-webkit-autofill":{
      WebkitTextFillColor:TEXT,
      WebkitBoxShadow:"0 0 0 1000px #FFFFFF inset"
    }
  };

  return(
    <Box sx={{
      position:"relative",
      minHeight:"100dvh",
      width:"100%",
      display:"flex",
      alignItems:"center",
      justifyContent:"center",
      overflowX:"hidden",
      boxSizing:"border-box",
      backgroundImage:`
        linear-gradient(
          135deg,
          rgba(20,50,56,0.30) 0%,
          rgba(20,50,56,0.18) 50%,
          rgba(20,50,56,0.34) 100%
        ),
        url("${background}")
      `,
      backgroundSize:"cover",
      backgroundPosition:"center center",
      backgroundRepeat:"no-repeat",
      backgroundAttachment:{xs:"scroll",md:"fixed"},
      px:{xs:2,sm:3},
      py:{xs:3,sm:4}
    }}>
      <Paper
        component="form"
        onSubmit={handleSubmit}
        elevation={0}
        sx={{
          position:"relative",
          width:"100%",
          maxWidth:420,
          borderRadius:{xs:"20px",sm:"24px"},
          backgroundColor:"rgba(255,255,255,0.96)",
          backdropFilter:"blur(18px)",
          WebkitBackdropFilter:"blur(18px)",
          border:"1px solid rgba(255,255,255,0.85)",
          boxShadow:"none",
          px:{xs:2.5,sm:4},
          py:{xs:3.5,sm:4.5},
          boxSizing:"border-box"
        }}
      >
        <Stack spacing={0} alignItems="center">
          <Box
            component="img"
            src={logo}
            alt="Yumim"
            sx={{
              display:"block",
              width:{xs:112,sm:125},
              height:{xs:112,sm:125},
              objectFit:"contain",
              mb:1.5
            }}
          />

          <Typography
            component="h1"
            sx={{
              fontSize:{xs:"1.4rem",sm:"1.55rem"},
              fontWeight:700,
              color:TEXT,
              textAlign:"center",
              letterSpacing:"-0.025em",
              lineHeight:1.3
            }}
          >
            Bienvenido a Yumim
          </Typography>

          <Typography sx={{
            mt:0.6,
            fontSize:"0.95rem",
            fontWeight:500,
            color:PRIMARY_DARK,
            textAlign:"center"
          }}>
            ¡Sonríe, hay postre!
          </Typography>

          <Box sx={{
            width:44,
            height:3,
            borderRadius:"10px",
            backgroundColor:PRIMARY,
            mt:2.2,
            mb:2.5
          }}/>

          <Typography sx={{
            fontSize:"1rem",
            fontWeight:600,
            color:TEXT,
            textAlign:"center",
            mb:0.5
          }}>
            Iniciar sesión
          </Typography>

          <Typography sx={{
            fontSize:"0.83rem",
            fontWeight:400,
            color:SECONDARY_TEXT,
            textAlign:"center",
            lineHeight:1.6,
            mb:3
          }}>
            Ingresa tus credenciales para continuar
          </Typography>
        </Stack>

        {error&&(
          <Alert
            severity="error"
            sx={{
              mb:2.5,
              borderRadius:"12px",
              fontSize:"0.83rem",
              fontWeight:400,
              backgroundColor:"#FFF4F2",
              border:"1px solid #F3D2CD",
              "& .MuiAlert-message":{
                overflowWrap:"anywhere"
              }
            }}
          >
            {error}
          </Alert>
        )}

        <Stack spacing={2.2}>
          <TextField
            fullWidth
            size="small"
            name="email"
            label="Correo electrónico"
            type="email"
            value={form.email}
            onChange={handleChange}
            autoComplete="email"
            disabled={loading}
            required
            sx={inputStyles}
            InputProps={{
              startAdornment:(
                <InputAdornment position="start">
                  <MailOutlineRounded sx={{
                    fontSize:19,
                    color:"#93A6AA"
                  }}/>
                </InputAdornment>
              )
            }}
          />

          <TextField
            fullWidth
            size="small"
            name="password"
            label="Contraseña"
            type={showPassword?"text":"password"}
            value={form.password}
            onChange={handleChange}
            autoComplete="current-password"
            disabled={loading}
            required
            sx={inputStyles}
            InputProps={{
              startAdornment:(
                <InputAdornment position="start">
                  <LockOutlined sx={{
                    fontSize:19,
                    color:"#93A6AA"
                  }}/>
                </InputAdornment>
              ),
              endAdornment:(
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    size="small"
                    disabled={loading}
                    aria-label={
                      showPassword
                        ?"Ocultar contraseña"
                        :"Mostrar contraseña"
                    }
                    onClick={()=>
                      setShowPassword(prev=>!prev)
                    }
                    sx={{
                      color:"#83969A",
                      "&:hover":{
                        color:PRIMARY_DARK
                      }
                    }}
                  >
                    {showPassword?(
                      <VisibilityOff sx={{fontSize:19}}/>
                    ):(
                      <Visibility sx={{fontSize:19}}/>
                    )}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disableElevation
            disabled={loading}
            sx={{
              height:46,
              mt:"5px !important",
              borderRadius:"12px",
              backgroundColor:PRIMARY_DARK,
              color:"#FFFFFF",
              textTransform:"none",
              fontSize:"0.92rem",
              fontWeight:600,
              letterSpacing:0,
              boxShadow:"none",
              "&:hover":{
                backgroundColor:"#287D84",
                boxShadow:"none"
              },
              "&.Mui-disabled":{
                backgroundColor:"#A5D4D7",
                color:"#FFFFFF"
              }
            }}
          >
            {loading?(
              <CircularProgress
                size={21}
                thickness={4}
                sx={{color:"#FFFFFF"}}
              />
            ):"Ingresar"}
          </Button>
        </Stack>

        <Box sx={{
          mt:3.2,
          pt:2.5,
          borderTop:"1px solid #E9EFF0"
        }}>
          <Typography sx={{
            fontSize:"0.74rem",
            fontWeight:400,
            lineHeight:1.6,
            color:"#91A0A4",
            textAlign:"center"
          }}>
            Acceso exclusivo para personal autorizado
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}
